import { Add, Save } from '@mui/icons-material';
import { Divider, MenuItem, Stack, Switch, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { StyledSelectField } from 'src/Common/Components/Forms/StyledSelectField';
import { StyledTextField } from 'src/Common/Components/Forms/StyledTextField';
import { CatalogPicker } from './CatalogPicker';
import { SystemTile } from './SystemTile';
import SectionDrawerContent from 'src/SettingsSection/Components/SectionDrawerContent';
import useDeviceIntegrations, {
  mapIntegrationTechnology,
} from 'src/DeviceIntegrationManagement/Providers/DeviceIntegrationProvider';
import { CoordinateInput } from 'src/Common/Components/CoordinateInput';
import { useSettingsSection } from 'src/SettingsSection/Providers/SettingsSectionProvider';
import { IntegrationConfiguration } from 'src/DeviceIntegrationManagement/MockData/types';
import { ConnectorFormField, getConnectorFormSchema } from 'src/DeviceIntegrationManagement/MockData/formSchema';

const SectionHeader = ({ title }: { title: string }): React.JSX.Element => (
  <Typography
    variant={'caption'}
    sx={{
      fontWeight: 700,
      letterSpacing: 0.6,
      textTransform: 'uppercase',
      color: 'text.secondary',
      fontSize: 11,
    }}
  >
    {title}
  </Typography>
);

type CredentialMode = 'single' | 'split';

const stringifyConfigValues = (configuration: IntegrationConfiguration): Record<string, string> => {
  const values: Record<string, string> = {};
  for (const [key, value] of Object.entries(configuration)) {
    if (value !== undefined) {
      values[key] = String(value);
    }
  }
  return values;
};

export const IntegrationFormDrawer = (): React.JSX.Element => {
  const { setDrawerOpen } = useSettingsSection();
  const { selectedIntegration, saveIntegration } = useDeviceIntegrations();
  const isEdit = selectedIntegration !== undefined;

  // Add mode starts on the catalog step; edit mode jumps straight to the form.
  const [connectorKey, setConnectorKey] = useState<string | undefined>(selectedIntegration?.integrationName);
  const [coordinate, setCoordinate] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [credentialMode, setCredentialMode] = useState<CredentialMode>('single');
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const touch = (field: string): void => setTouched((prev) => ({ ...prev, [field]: true }));
  const setValue = (key: string, value: string): void => setValues((prev) => ({ ...prev, [key]: value }));

  const schema = useMemo(
    () => (connectorKey !== undefined ? getConnectorFormSchema(connectorKey) : undefined),
    [connectorKey],
  );
  const technology = useMemo(
    () => (connectorKey !== undefined ? mapIntegrationTechnology(connectorKey) : undefined),
    [connectorKey],
  );

  const basicFields = useMemo(() => schema?.fields.filter((field) => !field.advanced) ?? [], [schema]);
  const advancedFields = useMemo(() => schema?.fields.filter((field) => field.advanced) ?? [], [schema]);

  const resetValues = useCallback(() => {
    setTouched({});
    if (schema === undefined) {
      setValues({});
    }

    setConnectorKey(selectedIntegration?.integrationName);
    setCoordinate(selectedIntegration?.coordinate ?? '');

    const config = selectedIntegration?.configuration ?? {};
    setValues(stringifyConfigValues(config as Record<string, string | number | undefined>));
    setCredentialMode(
      isEdit
        ? config.pickingPassword !== undefined || config.updatePassword !== undefined
          ? 'split'
          : 'single'
        : 'single',
    );
  }, [selectedIntegration, connectorKey]);

  // (Re-)initialize whenever the drawer opens or switches target.
  useEffect(() => {
    resetValues();
  }, []);

  const isCredentialsOk = useMemo(() => {
    if (!schema?.splitCredentials) {
      return true;
    }
    if (credentialMode === 'single') {
      return (values.password ?? '') !== '';
    }
    return (values.pickingPassword ?? '') !== '' && (values.updatePassword ?? '') !== '';
  }, [schema, credentialMode, values]);

  const isValid = useMemo(() => {
    if (connectorKey === undefined || schema === undefined) {
      return false;
    }
    if (coordinate.trim() === '') {
      return false;
    }
    const requiredOk = schema.fields
      .filter((field) => field.required)
      .every((field) => (values[field.key] ?? '').trim() !== '');
    return requiredOk && isCredentialsOk;
  }, [connectorKey, schema, coordinate, values, isCredentialsOk]);

  const onSave = (): void => {
    if (connectorKey === undefined || schema === undefined) {
      return;
    }

    const configuration: IntegrationConfiguration = {};
    for (const field of schema.fields) {
      const raw = (values[field.key] ?? '').trim();
      if (raw === '') {
        continue;
      }
      configuration[field.key] = field.type === 'number' ? Number(raw) : raw;
    }
    if (schema.splitCredentials) {
      if (credentialMode === 'single') {
        configuration.password = values.password;
      } else {
        configuration.pickingPassword = values.pickingPassword;
        configuration.updatePassword = values.updatePassword;
      }
    }

    saveIntegration(connectorKey, coordinate, configuration, () => setDrawerOpen(false));
  };

  const renderField = (field: ConnectorFormField): React.JSX.Element => {
    if (field.type === 'select') {
      return (
        <StyledSelectField
          key={field.key}
          label={field.label}
          value={values[field.key] ?? ''}
          onChange={(e) => setValue(field.key, e.target.value as string)}
        >
          {(field.options ?? []).map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </StyledSelectField>
      );
    }
    return (
      <StyledTextField
        key={field.key}
        label={field.required ? `${field.label} *` : field.label}
        type={field.type === 'password' ? 'password' : 'text'}
        numeric={field.type === 'number'}
        value={values[field.key] ?? ''}
        onChange={(e) => setValue(field.key, e.target.value)}
        onBlur={() => touch(field.key)}
        error={field.required && touched[field.key] && (values[field.key] ?? '').trim() === ''}
        helperText={field.helper}
        inputProps={field.type === 'password' ? { autoComplete: 'new-password' } : undefined}
      />
    );
  };

  const showCatalog = !isEdit && connectorKey === undefined;

  const contentHeader = (
    <Stack direction={'row'} alignItems={'center'} spacing={1.5} sx={{ minWidth: 0 }}>
      {technology && <SystemTile system={technology} size={36} />}
      <Stack spacing={0}>
        <Typography variant={'h6'} fontWeight={700} noWrap>
          {showCatalog
            ? 'Add integration'
            : isEdit
              ? `Edit ${technology?.name ?? ''}`
              : `Connect ${technology?.name ?? ''}`}
        </Typography>
        <Typography variant={'caption'} color={'text.secondary'} noWrap>
          {showCatalog ? 'Pick the system to connect to.' : technology ? `by ${technology?.manufacturer?.name}` : ''}
        </Typography>
      </Stack>
    </Stack>
  );

  const catalogContent = (
    <SectionDrawerContent header={contentHeader}>
      <CatalogPicker onPick={setConnectorKey} />
    </SectionDrawerContent>
  );

  const formContent = (
    <SectionDrawerContent
      header={contentHeader}
      onBack={!isEdit && !showCatalog ? () => setConnectorKey(undefined) : undefined}
      actions={{
        confirm: {
          onClick: () => {
            onSave();
          },
          icon: isEdit ? <Save /> : <Add />,
          disabled: !isValid,
          label: isEdit ? 'Save' : 'Connect',
        },
        reset: { onClick: resetValues },
      }}
    >
      <Stack spacing={4}>
        {/* Placement */}
        <Stack spacing={1.5}>
          <SectionHeader title={'Placement'} />
          <CoordinateInput
            disabled={isEdit}
            label={'Coordinate *'}
            value={coordinate}
            onChange={(c) => setCoordinate(c)}
            trailingDelimiter={false}
          />
        </Stack>

        {/* Connection */}
        {basicFields.length > 0 && (
          <Stack spacing={2}>
            <SectionHeader title={'Connection'} />
            {basicFields.map(renderField)}
          </Stack>
        )}
        {basicFields.length === 0 && !schema?.splitCredentials && (
          <Typography variant={'body2'} color={'text.secondary'}>
            This integration needs no connection configuration.
          </Typography>
        )}

        {/* Credentials (Sepioo-style single/split) */}
        {schema?.splitCredentials && (
          <Stack spacing={2}>
            <SectionHeader title={'Credentials'} />
            <Stack direction={'row'} alignItems={'center'} spacing={1}>
              <Switch
                size={'small'}
                checked={credentialMode === 'split'}
                onChange={(e) => setCredentialMode(e.target.checked ? 'split' : 'single')}
              />
              <Typography variant={'body2'} color={'text.secondary'}>
                Separate passwords for picking and update operations
              </Typography>
            </Stack>
            {credentialMode === 'single' ? (
              <StyledTextField
                label={'Password *'}
                type={'password'}
                value={values.password ?? ''}
                onChange={(e) => setValue('password', e.target.value)}
                onBlur={() => touch('password')}
                error={touched.password && (values.password ?? '') === ''}
                inputProps={{ autoComplete: 'new-password' }}
              />
            ) : (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ '& > *': { flex: 1, minWidth: 0 } }}>
                <StyledTextField
                  label={'Picking password *'}
                  type={'password'}
                  value={values.pickingPassword ?? ''}
                  onChange={(e) => setValue('pickingPassword', e.target.value)}
                  onBlur={() => touch('pickingPassword')}
                  error={touched.pickingPassword && (values.pickingPassword ?? '') === ''}
                  helperText={'Button / blink operations.'}
                  inputProps={{ autoComplete: 'new-password' }}
                />
                <StyledTextField
                  label={'Update password *'}
                  type={'password'}
                  value={values.updatePassword ?? ''}
                  onChange={(e) => setValue('updatePassword', e.target.value)}
                  onBlur={() => touch('updatePassword')}
                  error={touched.updatePassword && (values.updatePassword ?? '') === ''}
                  helperText={'Image / label operations.'}
                  inputProps={{ autoComplete: 'new-password' }}
                />
              </Stack>
            )}
          </Stack>
        )}

        {/* Advanced */}
        {advancedFields.length > 0 && (
          <Stack spacing={2}>
            <Divider />
            <SectionHeader title={'Advanced'} />
            {advancedFields.map(renderField)}
          </Stack>
        )}
      </Stack>
    </SectionDrawerContent>
  );

  return showCatalog ? catalogContent : formContent;
};
